import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Form, Spinner, Alert, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaQuestionCircle } from "react-icons/fa";
import { searchUsers, getCostCenterSuggestions } from "@/services/resourcesApi";
import { ResourceSummary } from "@/types";

interface Props {
  show: boolean;
  resources: ResourceSummary[];
  onUpdate: (updates: {
    primaryOwnerNtid?: string;
    secondaryOwnerNtid?: string;
    costCenter?: string;
    businessUnit?: string;
  }) => Promise<void>;
  onClose: () => void;
  loading?: boolean;
}

const BulkUpdateMetadataModal: React.FC<Props> = ({
  show,
  resources,
  onUpdate,
  onClose,
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    primaryOwnerNtid: "",
    secondaryOwnerNtid: "",
    costCenter: "",
    businessUnit: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeSearchField, setActiveSearchField] = useState<"primary" | "secondary" | null>(null);
  const [costCenterOptions, setCostCenterOptions] = useState<string[]>([]);
  const [costCenterLoading, setCostCenterLoading] = useState(false);
  const [showCostCenterSuggestions, setShowCostCenterSuggestions] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced user search
  const handleSearchUsers = (query: string, field: "primary" | "secondary") => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Don't search for empty queries or less than 2 characters
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setActiveSearchField(null);
      return;
    }

    setActiveSearchField(field);
    setSearchLoading(true);

    // Set new debounce timer (500ms)
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const results = await searchUsers(query);
        setSearchResults(results || []);
      } catch (err) {
        console.error("Search failed", err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 500);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!show) return;

    const loadCostCenters = async () => {
      try {
        setCostCenterLoading(true);
        setCostCenterOptions(await getCostCenterSuggestions());
      } catch (err) {
        console.error("Cost center lookup failed", err);
        setCostCenterOptions([]);
      } finally {
        setCostCenterLoading(false);
      }
    };

    void loadCostCenters();
  }, [show]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }

    // Trigger search for owner fields
    if (field === "primaryOwnerNtid") {
      handleSearchUsers(value, "primary");
    } else if (field === "secondaryOwnerNtid") {
      handleSearchUsers(value, "secondary");
    }
  };

  const selectUser = (user: any, field: "primaryOwnerNtid" | "secondaryOwnerNtid") => {
    const value = user.userPrincipalName || user.mail || user.displayName;
    const employeeDeptId = (user?.employeeDeptId || "").toString().trim();

    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "primaryOwnerNtid" && employeeDeptId ? { costCenter: employeeDeptId } : {})
    }));
    setSearchResults([]);
    setActiveSearchField(null);
  };

  const handlePrimaryOwnerBlur = async (email: string) => {
    const normalizedEmail = email.trim();
    if (!normalizedEmail.includes("@")) return;

    try {
      const users = await searchUsers(normalizedEmail);
      const user = users.find((candidate: any) =>
        [candidate.mail, candidate.userPrincipalName]
          .some((candidateEmail) => candidateEmail?.toLowerCase() === normalizedEmail.toLowerCase())
      );
      const employeeDeptId = (user?.employeeDeptId || "").toString().trim();

      if (employeeDeptId) {
        setFormData((prev) =>
          prev.primaryOwnerNtid.toLowerCase() === normalizedEmail.toLowerCase()
            ? { ...prev, costCenter: employeeDeptId }
            : prev
        );
      }
    } catch (err) {
      console.error("Primary owner department lookup failed", err);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // At least one field must be filled
    const hasAnyValue = Object.values(formData).some((v) => v && v.trim().length > 0);
    if (!hasAnyValue) {
      newErrors.form = "Please enter at least one field to update";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const updates = {
        ...(formData.primaryOwnerNtid && { primaryOwnerNtid: formData.primaryOwnerNtid }),
        ...(formData.secondaryOwnerNtid && { secondaryOwnerNtid: formData.secondaryOwnerNtid }),
        ...(formData.costCenter && { costCenter: formData.costCenter }),
        ...(formData.businessUnit && { businessUnit: formData.businessUnit }),
      };

      await onUpdate(updates);
      setFormData({
        primaryOwnerNtid: "",
        secondaryOwnerNtid: "",
        costCenter: "",
        businessUnit: "",
      });
    } catch (err) {
      console.error("Update failed", err);
    }
  };

  const filteredCostCenterOptions = costCenterOptions
    .filter((costCenter) => costCenter.toLowerCase().includes(formData.costCenter.trim().toLowerCase()))
    .slice(0, 12);

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Update Group Information</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-4 p-3 bg-light rounded">
          <strong>Selected Groups ({resources.length}):</strong>
          <div className="mt-2 small">
            {resources.length > 0 ? (
              resources.map((r) => (
                <div key={r.internalResourceId} className="mb-2 pb-2 border-bottom">
                  <div className="fw-semibold text-dark">{r.displayName}</div>
                  {r.url && (
                    <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                      URL: <a href={r.url} target="_blank" rel="noreferrer" className="text-muted">{r.url}</a>
                    </div>
                  )}
                  {r.primaryOwner && (
                    <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                      Owner: <strong>{r.primaryOwner}</strong>
                    </div>
                  )}
                  {!r.url && !r.primaryOwner && (
                    <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                      (No URL or Owner info available)
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-muted">No groups selected</div>
            )}
          </div>
        </div>

        {errors.form && <Alert variant="danger">{errors.form}</Alert>}

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Primary Owner</Form.Label>
            <div style={{ position: "relative" }}>
              <Form.Control
                type="text"
                placeholder="Start typing to search..."
                value={formData.primaryOwnerNtid}
                onChange={(e) => handleChange("primaryOwnerNtid", e.target.value)}
                onBlur={(e) => void handlePrimaryOwnerBlur(e.target.value)}
                isInvalid={!!errors.primaryOwnerNtid}
              />
              {activeSearchField === "primary" && searchResults.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "white",
                    border: "1px solid #dee2e6",
                    borderTop: "none",
                    maxHeight: "200px",
                    overflowY: "auto",
                    zIndex: 1000,
                  }}
                >
                  {searchLoading ? (
                    <div style={{ padding: "8px", textAlign: "center" }}>
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : (
                    searchResults.map((user) => (
                      <div
                        key={user.id}
                        onClick={() => selectUser(user, "primaryOwnerNtid")}
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          borderBottom: "1px solid #e9ecef",
                          fontSize: "0.9rem",
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLElement).style.backgroundColor = "#f8f9fa";
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLElement).style.backgroundColor = "white";
                        }}
                      >
                        <div className="fw-semibold">{user.displayName}</div>
                        <div className="small text-muted">{user.userPrincipalName || user.mail}</div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            {errors.primaryOwnerNtid && (
              <Form.Control.Feedback type="invalid">
                {errors.primaryOwnerNtid}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Secondary Owner</Form.Label>
            <div style={{ position: "relative" }}>
              <Form.Control
                type="text"
                placeholder="Start typing to search..."
                value={formData.secondaryOwnerNtid}
                onChange={(e) => handleChange("secondaryOwnerNtid", e.target.value)}
                isInvalid={!!errors.secondaryOwnerNtid}
              />
              {activeSearchField === "secondary" && searchResults.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "white",
                    border: "1px solid #dee2e6",
                    borderTop: "none",
                    maxHeight: "200px",
                    overflowY: "auto",
                    zIndex: 1000,
                  }}
                >
                  {searchLoading ? (
                    <div style={{ padding: "8px", textAlign: "center" }}>
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : (
                    searchResults.map((user) => (
                      <div
                        key={user.id}
                        onClick={() => selectUser(user, "secondaryOwnerNtid")}
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          borderBottom: "1px solid #e9ecef",
                          fontSize: "0.9rem",
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLElement).style.backgroundColor = "#f8f9fa";
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLElement).style.backgroundColor = "white";
                        }}
                      >
                        <div className="fw-semibold">{user.displayName}</div>
                        <div className="small text-muted">{user.userPrincipalName || user.mail}</div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            {errors.secondaryOwnerNtid && (
              <Form.Control.Feedback type="invalid">
                {errors.secondaryOwnerNtid}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="d-flex align-items-center gap-1">
              Cost Center / Dept ID
              <OverlayTrigger trigger={["hover", "focus"]} placement="top" overlay={<Tooltip id="bulk-cost-center-help">Cost Center / Dept ID is usually the Cost Center / Dept ID for the Primary Owner.</Tooltip>}>
                <button type="button" className="btn btn-link btn-sm p-0 text-muted d-inline-flex align-items-center" aria-label="Cost Center / Dept ID help">
                  <FaQuestionCircle />
                </button>
              </OverlayTrigger>
            </Form.Label>
            <div className="position-relative">
              <Form.Control
                type="text"
                value={formData.costCenter}
                onChange={(e) => {
                  handleChange("costCenter", e.target.value);
                  setShowCostCenterSuggestions(true);
                }}
                onFocus={() => setShowCostCenterSuggestions(true)}
                onBlur={() => window.setTimeout(() => setShowCostCenterSuggestions(false), 150)}
                isInvalid={!!errors.costCenter}
                disabled={costCenterLoading}
                placeholder={costCenterLoading ? "Loading Cost Center / Dept IDs..." : "Type or select Cost Center / Dept ID"}
                autoComplete="off"
              />
              {showCostCenterSuggestions && !costCenterLoading && (
                <div className="position-absolute bg-white border rounded shadow-sm w-100 mt-1" style={{ zIndex: 1060, maxHeight: 240, overflowY: "auto" }}>
                  {filteredCostCenterOptions.length > 0 ? (
                    filteredCostCenterOptions.map((costCenter) => (
                      <button
                        key={costCenter}
                        type="button"
                        className="dropdown-item py-2"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleChange("costCenter", costCenter);
                          setShowCostCenterSuggestions(false);
                        }}
                      >
                        {costCenter}
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-2 small text-muted">No matching Dept IDs. You can keep the typed value.</div>
                  )}
                </div>
              )}
            </div>
            {errors.costCenter && (
              <Form.Control.Feedback type="invalid" className="d-block">
                {errors.costCenter}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Business Unit</Form.Label>
            <Form.Select
              value={formData.businessUnit}
              onChange={(e) => handleChange("businessUnit", e.target.value)}
              isInvalid={!!errors.businessUnit}
            >
              <option value="">-- Select Business Unit --</option>
              <option value="ENMAX Corporation">ENMAX Corporation</option>
              <option value="ENMAX Energy">ENMAX Energy</option>
              <option value="ENMAX Power">ENMAX Power</option>
            </Form.Select>
            {errors.businessUnit && (
              <Form.Control.Feedback type="invalid">
                {errors.businessUnit}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Form>

        <div className="alert alert-info small mt-3">
          <strong>Note:</strong> Leave fields blank to keep existing values unchanged.
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" /> Updating...
            </>
          ) : (
            "Update Groups"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkUpdateMetadataModal;
